import { Key, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AccountIdCard } from "@/components/common/AccountIdCard";
import { EmptyState } from "@/components/common/empty-state";
import { SkeletonItem } from "@/components/common/skeleton-item";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreateApiKeyButton } from "@/features/apiKeys/components/CreateApiKeyButton";
import { DeleteApiKeyDialog } from "@/features/apiKeys/components/DeleteApiKeyDialog";
import { useOrgApiKeys } from "@/features/apiKeys/hooks/useApiKeys";

export const ApiKeyList = () => {
  const { data: apiKeys, isLoading, error, isError } = useOrgApiKeys();
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error("Failed to load API keys", {
        description:
          error instanceof Error
            ? error.message
            : "Please try again or contact support",
      });
    }
  }, [isLoading, isError, error]);

  return (
    <div className="dashboard-page">
      <div className="page-heading">
        <div>
          <h1 className="page-title">API Keys</h1>
          <p className="mt-2.5 text-sm text-muted-foreground">
            Create and manage keys for your API requests.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <AccountIdCard variant="compact" />
        <Card>
          <CardHeader className="border-b flex flex-row flex-wrap items-center justify-between gap-4">
            <CardTitle>Your API Keys</CardTitle>
            <CreateApiKeyButton />
          </CardHeader>
          <CardContent className="px-6">
            {isLoading ? (
              <SkeletonItem />
            ) : apiKeys?.length === 0 ? (
              <EmptyState
                icon={Key}
                title="No API keys"
                description="Get started by creating a new API key."
              />
            ) : (
              <div className="divide-y divide-border">
                {apiKeys?.map((key) => (
                  <div
                    key={key.id}
                    className="flex min-h-22 items-center justify-between gap-4 py-4 text-sm"
                  >
                    <div className="min-w-0 space-y-1 break-words">
                      <p className="font-medium">{key.name}</p>
                      <p className="text-[13px] text-muted-foreground">
                        {key.key_preview}
                      </p>
                      {key.last_used_at && (
                        <p className="text-[13px] text-muted-foreground">
                          Last used:{" "}
                          {new Date(key.last_used_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Delete API Key"
                      className="size-11 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => setDeleteKeyId(key.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <DeleteApiKeyDialog
        keyId={deleteKeyId}
        onClose={() => setDeleteKeyId(null)}
      />
    </div>
  );
};
